import { type CopilotErrorEvent } from '@copilotkit/shared'
import { AUTH_SESSION_MISSING } from 'shared/api/authSession'

/**
 * Flow rejects a bad `cirrus_source` with HTTP 400 and a JSON body naming the problem —
 * unknown collection, unknown `kind`, a `kind` from the wrong collection, mismatched or
 * non-numeric `client_id`, failed user-token exchange.
 *
 * These are deterministic: the same payload fails identically, so there is nothing to
 * retry and no point silently falling back to sending no data source. The message is
 * pulled out and surfaced verbatim so the user can see which part of the selection Flow
 * refused, instead of the generic "something went wrong" toast.
 */
const MESSAGE_KEYS = ['message', 'detail', 'error', 'error_message'] as const

/**
 * Run errors reach `onError` as a flat string rather than a structured response — the
 * status only exists as this prefix, e.g. `HTTP 400: {"error":{"message":"..."}}`.
 */
const HTTP_STATUS_PREFIX = /^\s*(?:API Error|HTTP)\s+(\d{3})\s*:\s*/i

const readMessageFromBody = (body: unknown): string | null => {
  if (typeof body === 'string') {
    const trimmed = body.trim()
    if (trimmed === '') {
      return null
    }
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
      return trimmed
    }
    try {
      return readMessageFromBody(JSON.parse(trimmed))
    } catch {
      return trimmed
    }
  }

  if (Array.isArray(body)) {
    const messages = body.map(readMessageFromBody).filter((message): message is string => message != null)
    return messages.length > 0 ? messages.join('; ') : null
  }

  if (body == null || typeof body !== 'object') {
    return null
  }

  for (const key of MESSAGE_KEYS) {
    const message = readMessageFromBody((body as Record<string, unknown>)[key])
    if (message != null) {
      return message
    }
  }

  return null
}

/** Every place a run error might carry its text, most specific first. */
const collectErrorTexts = (event: CopilotErrorEvent): string[] => {
  const context = event.context as { event?: { message?: unknown } } | undefined
  return [event.error?.message, context?.event?.message, event.error].filter(
    (candidate): candidate is string => typeof candidate === 'string'
  )
}

/**
 * The user-facing reason a Copilot run was rejected as a bad request, or `null` when the
 * failure is not a `400` we can explain (caller should fall back to the generic message).
 *
 * Only `400` qualifies: a `500` (Flow down, session creation failed) is transient and
 * refreshing genuinely can help, which is what the generic copy tells the user to do.
 */
export const getCopilotBadRequestMessage = (event: CopilotErrorEvent): string | null => {
  if (event.context?.response?.status === 400) {
    return readMessageFromBody(event.context.response.body) ?? readMessageFromBody(event.error?.message) ?? null
  }

  for (const text of collectErrorTexts(event)) {
    const match = HTTP_STATUS_PREFIX.exec(text)
    if (match?.[1] === '400') {
      return readMessageFromBody(text.slice(match[0].length)) ?? text
    }
  }

  return null
}

const SESSION_MISSING_KEYS = ['error', 'payload', 'body', 'message', 'detail'] as const

/** CopilotKit puts the runtime body on the error string or payload, not on ApiClientError. */
const containsAuthSessionMissing = (value: unknown, depth = 0): boolean => {
  if (depth > 6) {
    return false
  }

  if (typeof value === 'string') {
    if (value.includes(AUTH_SESSION_MISSING)) {
      return true
    }
    const trimmed = value.trim()
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
      return false
    }
    try {
      return containsAuthSessionMissing(JSON.parse(trimmed), depth + 1)
    } catch {
      return false
    }
  }

  if (value == null || typeof value !== 'object') {
    return false
  }

  if (Array.isArray(value)) {
    return value.some((item) => containsAuthSessionMissing(item, depth + 1))
  }

  const record = value as Record<string, unknown>
  if (record.code === AUTH_SESSION_MISSING) {
    return true
  }

  return SESSION_MISSING_KEYS.some((key) => containsAuthSessionMissing(record[key], depth + 1))
}

/**
 * True when a CopilotKit run failed because POST /api/copilot reported a missing session.
 * That failure never reaches the react-query client-error handler.
 */
export const isCopilotSessionMissing = (event: CopilotErrorEvent): boolean => {
  const payload =
    event.error != null && typeof event.error === 'object' && 'payload' in event.error ? event.error.payload : undefined

  return [event.error, event.context?.response?.body, payload, ...collectErrorTexts(event)].some((candidate) =>
    containsAuthSessionMissing(candidate)
  )
}
